import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Star, ArrowUpRight, Sparkles, ShieldCheck, Clock, MapPin, Leaf, PackageCheck, BadgeCheck } from "lucide-react";
import { MaskLines, Reveal, ClipImage } from "@/components/motion";
import { CTAButton, ArrowLink } from "@/components/CTA";
import Marquee from "@/components/Marquee";
import Seo from "@/components/Seo";
import Faq from "@/components/Faq";
import { BUSINESS, IMAGES, SERVICES, CHAPTERS, TESTIMONIALS, PRICING, CITIES } from "@/data";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} data-testid="hero" className="relative overflow-hidden pt-36 pb-16 lg:pt-44 lg:pb-24">
      <div className="mx-auto grid max-w-[1400px] items-end gap-12 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-8 inline-flex items-center gap-2 border border-[var(--line)] bg-[var(--brand-light)] px-4 py-2"
          >
            <Sparkles className="h-4 w-4 text-[var(--brand)]" />
            <span className="overline text-[var(--brand)]">Kitchener · Waterloo · Cambridge</span>
          </motion.div>

          <h1 className="font-display text-[3.4rem] font-extrabold leading-[0.92] tracking-tighter sm:text-7xl lg:text-[6.2rem]">
            <MaskLines lines={["A spotless", "home, without"]} delay={0.1} />
            <MaskLines lines={["the lifting."]} lineClass="text-[var(--brand)]" delay={0.34} />
          </h1>

          <Reveal delay={0.6} className="mt-8 max-w-xl text-lg leading-relaxed text-[var(--ink-soft)]">
            Friendly, reliable, professional cleaners for homes and offices across the Tri-Cities. Book in minutes and
            come home to something magical.
          </Reveal>

          <Reveal delay={0.74} className="mt-10 flex flex-wrap items-center gap-4">
            <CTAButton to="/contact" testId="hero-book-btn">Book Now <ArrowUpRight className="h-4 w-4" /></CTAButton>
            <CTAButton to="/contact" variant="ghost" testId="hero-quote-btn">Get Free Quote</CTAButton>
            <a href={`tel:${BUSINESS.phoneHref}`} data-testid="hero-call-btn" className="nav-link text-sm font-semibold text-[var(--ink)]">
              Call {BUSINESS.phoneDisplay}
            </a>
          </Reveal>

          <Reveal delay={0.9} className="mt-12 flex items-center gap-6">
            <div className="flex items-center gap-1 text-[var(--brand)]">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
            </div>
            <p className="text-sm text-[var(--ink-soft)]"><span className="font-semibold text-[var(--ink)]">4.9/5</span> from 300+ local cleans</p>
          </Reveal>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y }} className="relative">
            <ClipImage src={IMAGES.heroLiving} alt="Bright, freshly cleaned modern living room in Kitchener" className="aspect-[3/4] w-full" />
          </motion.div>
          <Reveal delay={0.7} className="absolute -bottom-6 -left-6 hidden bg-white p-6 shadow-[0_20px_50px_rgb(0,0,0,0.08)] sm:block">
            <p className="overline text-[var(--brand)]">First clean</p>
            <p className="mt-2 font-display text-4xl font-extrabold tracking-tight">20% off</p>
            <p className="mt-1 text-sm text-[var(--ink-soft)]">New customer discount</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const TRUST = [
  { icon: ShieldCheck, title: "Insured & vetted", body: "Every cleaner is background-checked and fully insured." },
  { icon: Clock, title: "On-time, always", body: "Reliable scheduling with the same trusted team." },
  { icon: MapPin, title: "Locally owned", body: "Proudly serving Kitchener, Waterloo & Cambridge homes and offices." },
];

const ASSURANCES = [
  { icon: Leaf, title: "Eco-friendly products", body: "Safe, non-toxic supplies that are kind to your family, pets and the planet." },
  { icon: PackageCheck, title: "We bring everything", body: "All cleaning supplies and equipment included — you don't lift a finger." },
  { icon: BadgeCheck, title: "Free estimates", body: "Fast, no-obligation quotes with clear, upfront pricing every time." },
  { icon: Sparkles, title: "Satisfaction guaranteed", body: "Not happy with a spot? We'll come back and make it right — guaranteed." },
];

export default function Home() {
  return (
    <main data-testid="home-page">
      <Seo
        title="Cleaning Services Kitchener, Waterloo & Cambridge | Magical Cleaning Team"
        description="Affordable house cleaning, deep cleaning, move-out cleaning & office cleaning across Kitchener, Waterloo & Cambridge. Friendly, reliable local cleaners. 20% off your first clean — get a free quote."
        path="/"
      />
      <Hero />

      <section className="border-y border-[var(--line)] bg-[var(--bg-soft)]">
        <div className="mx-auto grid max-w-[1400px] gap-px bg-[var(--line)] px-0 sm:grid-cols-3">
          {TRUST.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.08} className="bg-white p-8 lg:p-10">
              <t.icon className="h-7 w-7 text-[var(--brand)]" />
              <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{t.title}</h3>
              <p className="mt-2 text-sm text-[var(--ink-soft)]">{t.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Manifesto chapters */}
      <section data-testid="manifesto" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-36">
        <Reveal className="max-w-2xl">
          <p className="overline text-[var(--brand)]">Why us</p>
          <h2 className="mt-5 font-display text-4xl font-extrabold leading-[0.95] tracking-tighter sm:text-5xl lg:text-6xl">
            Cleaning, done the way it should be.
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-px bg-[var(--line)] lg:grid-cols-3">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.1} className="bg-white p-8 lg:p-10">
              <span className="font-mono-accent text-sm text-[var(--brand)]">{c.num}</span>
              <h3 className="mt-6 font-display text-2xl font-extrabold tracking-tight">{c.title}</h3>
              <p className="mt-4 leading-relaxed text-[var(--ink-soft)]">{c.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee />

      {/* Home & Business assurances */}
      <section data-testid="assurances" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="overline text-[var(--brand)]">Home & business cleaning</p>
            <h2 className="mt-5 font-display text-4xl font-extrabold leading-[0.95] tracking-tighter sm:text-5xl">
              Everything included. Nothing to worry about.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-[var(--ink-soft)]">
              Whether it's your home or your workplace, we bring the supplies, the checklist and the care — so you get
              a spotless space with zero hassle.
            </p>
            <CTAButton to="/contact" className="mt-9" testId="assurances-cta">Get Free Quote <ArrowUpRight className="h-4 w-4" /></CTAButton>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7">
            <div className="grid gap-px bg-[var(--line)] sm:grid-cols-2">
              {ASSURANCES.map((a, i) => (
                <Reveal key={a.title} delay={i * 0.08} className="bg-white p-8">
                  <a.icon className="h-7 w-7 text-[var(--brand)]" />
                  <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{a.title}</h3>
                  <p className="mt-2 text-sm text-[var(--ink-soft)]">{a.body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Services preview */}
      <section data-testid="services-preview" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className="overline text-[var(--brand)]">What we do</p>
            <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">Our services</h2>
          </Reveal>
          <Reveal><ArrowLink to="/services" testId="services-view-all">View all services</ArrowLink></Reveal>
        </div>

        <div className="mt-14 border-t border-[var(--line)]">
          {SERVICES.map((s, i) => (
            <Link
              key={s.id}
              to="/services"
              data-testid={`service-row-${s.id}`}
              className="group relative grid grid-cols-12 items-center gap-4 border-b border-[var(--line)] py-8 transition-colors duration-300 hover:bg-[var(--ink)]"
            >
              <span className="col-span-2 font-mono-accent text-sm text-[var(--brand)] transition-colors group-hover:text-[var(--mint)] lg:col-span-1">{s.num}</span>
              <h3 className="col-span-8 font-display text-2xl font-extrabold tracking-tight transition-colors group-hover:text-white sm:text-3xl lg:col-span-6">{s.title}</h3>
              <span className="col-span-12 hidden text-sm text-[var(--ink-soft)] transition-colors group-hover:text-white/60 lg:col-span-4 lg:block">{s.tag}</span>
              <ArrowUpRight className="col-span-2 ml-auto h-6 w-6 text-[var(--ink)] transition-all duration-300 group-hover:text-[var(--mint)] group-hover:translate-x-1 lg:col-span-1" />
            </Link>
          ))}
        </div>
      </section>

      {/* Big image band */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <ClipImage src={IMAGES.kitchen} alt="Professional cleaner wiping a kitchen counter in Waterloo" className="aspect-[16/9] w-full" />
      </section>

      {/* Service areas */}
      <section data-testid="service-areas" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
        <Reveal className="max-w-2xl">
          <p className="overline text-[var(--brand)]">Service areas</p>
          <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">Where we clean</h2>
          <p className="mt-6 text-lg text-[var(--ink-soft)]">Local, dedicated pages for every city we serve across the Tri-Cities.</p>
        </Reveal>
        <div className="mt-14 grid gap-px bg-[var(--line)] lg:grid-cols-3">
          {CITIES.map((c, i) => (
            <Link
              key={c.slug}
              to={`/${c.slug}`}
              data-testid={`area-card-${c.slug}`}
              className="group flex flex-col bg-white p-8 transition-colors duration-300 hover:bg-[var(--ink)] lg:p-10"
            >
              <MapPin className="h-7 w-7 text-[var(--brand)] transition-colors group-hover:text-[var(--mint)]" />
              <h3 className="mt-6 font-display text-3xl font-extrabold tracking-tight transition-colors group-hover:text-white">{c.name}</h3>
              <p className="mt-3 flex-1 text-sm text-[var(--ink-soft)] transition-colors group-hover:text-white/60">{c.stat}.</p>
              <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-[var(--brand)] transition-colors group-hover:text-[var(--mint)]">
                View {c.name} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
        <Reveal className="max-w-2xl">
          <p className="overline text-[var(--brand)]">Kind words</p>
          <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">Loved by locals</h2>
        </Reveal>
        <div className="mt-14 grid gap-8 lg:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1} className="flex flex-col border border-[var(--line)] p-8">
              <div className="flex gap-1 text-[var(--brand)]">
                {[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="mt-6 flex-1 font-display text-xl font-medium leading-snug tracking-tight">“{t.quote}”</p>
              <p className="mt-6 text-sm font-semibold">{t.name} <span className="text-[var(--ink-soft)]">· {t.city}</span></p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Pricing teaser */}
      <section data-testid="pricing" className="border-t border-[var(--line)] bg-[var(--brand-light)]/40">
        <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <Reveal>
              <p className="overline text-[var(--brand)]">Simple pricing</p>
              <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">Affordable, upfront rates</h2>
            </Reveal>
            <Reveal>
              <span className="inline-flex items-center gap-2 bg-[var(--mint)] px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[var(--ink)]">
                <Sparkles className="h-4 w-4" /> 20% off your first clean
              </span>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-px border border-[var(--line)] bg-[var(--line)] lg:grid-cols-3">
            {PRICING.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08} className={`relative flex flex-col bg-white p-8 lg:p-10 ${p.best ? "ring-1 ring-inset ring-[var(--brand)]" : ""}`}>
                {p.best && <span className="absolute right-6 top-8 bg-[var(--brand)] px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white">Popular</span>}
                <p className="overline text-[var(--brand)]">{p.name}</p>
                <div className="mt-6 flex items-end gap-2">
                  <span className="font-display text-6xl font-extrabold tracking-tighter">${p.price}</span>
                  <span className="mb-2 text-sm text-[var(--ink-soft)]">{p.unit}</span>
                </div>
                <ul className="mt-8 flex-1 space-y-3 text-sm text-[var(--ink-soft)]">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-3">
                      <span className="h-1.5 w-1.5 shrink-0 bg-[var(--brand)]" /> {f}
                    </li>
                  ))}
                </ul>
                <CTAButton to="/contact" variant={p.best ? "solid" : "ghost"} className="mt-8 w-full" testId={`pricing-cta-${p.id}`}>Book Now</CTAButton>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-sm text-[var(--ink-soft)]">* Final quote depends on home size and condition. Get a free, no-obligation estimate today.</p>
        </div>
      </section>

      {/* Commercial / office highlight */}
      <section data-testid="commercial" className="border-t border-[var(--line)] bg-[var(--ink)] text-white">
        <div className="mx-auto grid max-w-[1400px] items-stretch gap-0 lg:grid-cols-2">
          <div className="flex flex-col justify-center px-6 py-16 lg:px-10 lg:py-24">
            <Reveal>
              <p className="overline text-[var(--mint)]">Commercial & office cleaning</p>
              <h2 className="mt-5 font-display text-4xl font-extrabold leading-[0.95] tracking-tighter sm:text-5xl">
                Clean workspaces, happier teams.
              </h2>
              <p className="mt-6 max-w-md text-lg leading-relaxed text-white/60">
                Keep your office, studio or storefront spotless with dependable commercial cleaning — scheduled
                after-hours so your business never skips a beat. Desks, common areas, washrooms and floors, handled.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <CTAButton to="/contact" testId="commercial-quote">Get Free Quote <ArrowUpRight className="h-4 w-4" /></CTAButton>
                <a href={`tel:${BUSINESS.phoneHref}`} data-testid="commercial-call" className="sweep-btn inline-flex items-center gap-2 border border-white/25 px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-transform hover:-translate-y-0.5">
                  <span className="relative z-10">Call Us Today</span>
                </a>
              </div>
            </Reveal>
          </div>
          <div className="relative min-h-[320px] overflow-hidden lg:min-h-full">
            <img src={IMAGES.office} alt="Clean modern office workspace in Kitchener" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
          </div>
        </div>
      </section>

      <Faq />

      {/* Final CTA */}
      <section data-testid="home-cta" className="relative overflow-hidden bg-[var(--ink)] text-white">
        <div className="noise-layer pointer-events-none absolute inset-0 opacity-[0.05]" />
        <div className="relative mx-auto max-w-[1400px] px-6 py-24 text-center lg:px-10 lg:py-36">
          <Reveal>
            <h2 className="mx-auto max-w-4xl font-display text-4xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
              Ready for a spotless space?
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mx-auto mt-6 max-w-xl text-lg text-white/60">
            Book your clean today and enjoy 20% off as a first-time customer.
          </Reveal>
          <Reveal delay={0.2} className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <CTAButton to="/contact" testId="cta-book-btn">Book Now</CTAButton>
            <a href={`tel:${BUSINESS.phoneHref}`} data-testid="cta-call-btn" className="sweep-btn inline-flex items-center gap-2 border border-white/25 px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-transform hover:-translate-y-0.5">
              <span className="relative z-10">Call Us Today</span>
            </a>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
